import { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Plus, ArrowUpDown, Edit, Trash2, X, PlayCircle, Calendar, Clock, Loader2, RefreshCw, User as UserIcon, Sparkles, ChevronRight } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/shared/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/shared/components/ui/alert-dialog";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { Badge } from "@/shared/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { useCustomers, Customer } from "@/shared/hooks/useCustomers";
import { useToast } from "@/shared/hooks/use-toast";
import { useSubscriptionGuard } from "@/shared/hooks/useSubscriptionGuard";
import { useSubscription } from "@/features/billing/contexts/SubscriptionContext";
import { SubscriptionRequiredScreen } from "@/features/billing/components/SubscriptionRequiredScreen";
import { VideoPlayerModal } from "@/shared/components/VideoPlayerModal";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/shared/contexts/AuthContext";
import { apiClient } from "@/shared/services/apiClient";
import { API_ROUTES } from "@/shared/services/apiRoutes";
import { useTeamMembers } from "@/shared/hooks/useTeamMembers";

interface CallRecording {
  id: string;
  room_id: string;
  url: string;
  duration_seconds: number | null;
  created_at: string;
  customer_name: string | null;
  customer_phone: string | null;
}

export default function ContactsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { canAccess, reason } = useSubscriptionGuard();
  const { user, company } = useAuth();
  const { usage } = useSubscription();

  // Data loading
  const { customers, loading, refreshing, error: customersError, refresh, createCustomer, updateCustomer, deleteCustomer } = useCustomers();

  // Local state
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>('asc');

  // Team filtering state
  const [selectedTeamMember, setSelectedTeamMember] = useState<string>('all');
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<'admin' | 'user' | null>(null);
  const { teamMembers } = useTeamMembers();

  // Modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Add form
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  const [assignTo, setAssignTo] = useState("");

  // Edit form
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editPhoneNumber, setEditPhoneNumber] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editCountryCode, setEditCountryCode] = useState("+1");
  const [editAssignTo, setEditAssignTo] = useState("");

  // Call history
  const [callHistory, setCallHistory] = useState<CallRecording[]>([]);
  const [loadingCallHistory, setLoadingCallHistory] = useState(false);
  const [callHistoryOffset, setCallHistoryOffset] = useState(0);
  const [hasMoreCalls, setHasMoreCalls] = useState(false);

  // Video playback
  const [showVideoOverlay, setShowVideoOverlay] = useState(false);
  const [selectedRecording, setSelectedRecording] = useState<CallRecording | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);

  // Call details modal
  const [selectedCallForDetails, setSelectedCallForDetails] = useState<any | null>(null);
  const [aiSummary, setAiSummary] = useState<string[]>([]);
  const [aiTranscript, setAiTranscript] = useState<string | null>(null);
  const [callNotes, setCallNotes] = useState<string | null>(null);
  const [snapshots, setSnapshots] = useState<Array<{ name: string; url: string }>>([]);
  const [loadingCallData, setLoadingCallData] = useState(false);
  const [allRecordings, setAllRecordings] = useState<Array<{ id: string; url: string | null; duration_seconds: number | null; status: string | null; created_at: string }>>([]);

  const [isSaving, setIsSaving] = useState(false);

  const INITIAL_CALLS = 5;
  const LOAD_MORE_CALLS = 10;

  /**
   * Format phone number for display
   * Keeps +1 prefix and formats properly
   */
  const formatPhoneDisplay = (phone: string) => {
    // If phone already has +1 and proper format, return as is
    if (phone.match(/^\+1\s?\d{10}$/)) {
      return phone;
    }
    // Return as is - don't strip country code
    return phone;
  };

  /**
   * Sanitize phone input - remove non-digits
   */
  const sanitizePhoneInput = (value: string) => {
    return value.replace(/\D/g, '');
  };

  /**
   * Validate phone number (must be exactly 10 digits)
   */
  const isValidPhone = (value: string) => {
    const digitsOnly = value.replace(/\D/g, '');
    return digitsOnly.length === 10;
  };

  /**
   * Validate email
   */
  const isValidEmail = (value: string) => {
    if (!value) return true; // Optional
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(value.toLowerCase());
  };

  /**
   * Search and filter customers
   */
  const filteredAndSortedCustomers = useMemo(() => {
    let filtered = [...customers];

    // Apply team member filter
    if (currentUserId && selectedTeamMember !== 'all') {
      // Show customers assigned to specific team member
      // If assigned_to is null, fall back to created_by for backward compatibility
      filtered = filtered.filter(customer =>
        customer.assigned_to === selectedTeamMember ||
        (!customer.assigned_to && customer.created_by === selectedTeamMember)
      );
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(customer =>
        customer.name?.toLowerCase().includes(query) ||
        customer.email?.toLowerCase().includes(query) ||
        customer.phone_number?.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    if (sortOrder) {
      filtered.sort((a, b) => {
        const comparison = a.name.toLowerCase().localeCompare(b.name.toLowerCase());
        return sortOrder === 'asc' ? comparison : -comparison;
      });
    }

    return filtered;
  }, [customers, searchQuery, sortOrder, selectedTeamMember, currentUserId]);

  /**
   * Toggle sort order
   */
  const toggleSort = () => {
    setSortOrder(prev => {
      if (prev === null) return 'asc';
      if (prev === 'asc') return 'desc';
      return null;
    });
  };

  /**
   * Open customer detail modal
   */
  const handleCustomerClick = (customer: Customer) => {
    setSelectedCustomer(customer);
    setIsDetailModalOpen(true);
    setIsEditMode(false);

    // Load call history
    fetchCallHistory(customer.phone_number);
  };

  /**
   * Fetch call history for customer
   * Uses backend API to get all call types (sessions, recording requests, scheduled calls)
   */
  const fetchCallHistory = async (phone: string, offset = 0, append = false) => {
    setLoadingCallHistory(true);

    try {
      // Normalize phone for comparison
      const normalizedPhone = phone.replace(/\s/g, '');

      // Fetch from backend call history API
      const response = await apiClient.get<{
        ok: boolean;
        calls: any[];
        hasMore: boolean;
      }>(API_ROUTES.CALL_HISTORY, {
        params: { offset: 0, limit: 200 } // Fetch more to filter client-side
      });

      if (!response.ok || !response.calls) {
        setCallHistory([]);
        setHasMoreCalls(false);
        return;
      }

      // Filter calls for this specific customer by phone number
      const customerCalls = response.calls.filter((call: any) => {
        const callPhone = (call.phone_number || call.customer_phone || '').replace(/\s/g, '');
        return callPhone === normalizedPhone;
      });

      // Map to CallRecording format - include ALL calls (with or without recordings)
      const recordings = customerCalls.map((call: any) => ({
        id: call.id,
        room_id: call.room_id,
        url: call.recording_url || call.url,
        created_at: call.start_time || call.created_at,
        duration_seconds: call.duration_seconds || call.duration,
        customer_name: call.customer_name,
        customer_phone: call.phone_number || call.customer_phone,
        has_recording: call.has_recording,
        type: call.type, // 'call-session', 'recording-request', 'scheduled-call'
      }));

      if (append) {
        setCallHistory(prev => [...prev, ...(recordings as unknown as CallRecording[] || [])]);
      } else {
        setCallHistory(recordings as unknown as CallRecording[] || []);
      }

      setCallHistoryOffset(offset);
      setHasMoreCalls(false); // No pagination for now since we fetch all

    } catch (err) {
      console.error('Error loading call history:', err);
      toast({
        title: "Error",
        description: "Failed to load call history",
        variant: "destructive",
      });
    } finally {
      setLoadingCallHistory(false);
    }
  };

  /**
   * Load more calls
   */
  const loadMoreCalls = () => {
    if (selectedCustomer && hasMoreCalls && !loadingCallHistory) {
      fetchCallHistory(selectedCustomer.phone_number, callHistoryOffset + LOAD_MORE_CALLS, true);
    }
  };

  /**
   * Handle call click - opens call details modal
   */
  const handleCallClick = async (call: any) => {
    // Close customer details modal
    setIsDetailModalOpen(false);
    setSelectedCustomer(null);

    // Reset state
    setVideoUrl(null);
    setAiSummary([]);
    setAiTranscript(null);
    setCallNotes(null);
    setSnapshots([]);
    setAllRecordings([]);

    try {
      setLoadingCallData(true);
      setSelectedCallForDetails(call);

      // Call backend API to get all call details
      const response = await apiClient.get<{
        ok: boolean;
        session: any;
        recording: { id?: string; url: string; duration_seconds: number; status: string; created_at?: string } | null;
        recordings?: Array<{ id: string; url: string | null; duration_seconds: number | null; status: string | null; created_at: string }>;
        snapshots: Array<{ name: string; url: string }>;
      }>(API_ROUTES.CALL_DETAILS(call.room_id));

      if (response.ok) {
        // Set AI summary
        if (response.session?.ai_summary) {
          try {
            const summaryArray = typeof response.session.ai_summary === 'string'
              ? JSON.parse(response.session.ai_summary)
              : response.session.ai_summary;
            setAiSummary(Array.isArray(summaryArray) ? summaryArray : []);
          } catch {
            setAiSummary([]);
          }
        }

        // Set AI transcript
        if (response.session?.ai_transcript) {
          setAiTranscript(response.session.ai_transcript);
        }

        // Set notes
        if (response.session?.notes) {
          setCallNotes(response.session.notes);
        }

        // Set snapshots
        if (response.snapshots && response.snapshots.length > 0) {
          setSnapshots(response.snapshots);
        }

        // Set all recordings (for multiple recordings support)
        const hasRecordingFromDetails = (response.recordings && response.recordings.length > 0) || !!(response.recording && response.recording.url);
        if (response.recordings && response.recordings.length > 0) {
          setAllRecordings(response.recordings);
          // Set first recording URL for backwards compatibility
          setVideoUrl(response.recordings[0].url);
        } else if (response.recording && response.recording.url) {
          // Fallback to single recording for backwards compatibility
          setVideoUrl(response.recording.url);
          setAllRecordings([]);
        }

        // Update selectedCallForDetails with fresh recording status
        setSelectedCallForDetails((prev: any) => prev ? {
          ...prev,
          has_recording: prev.has_recording || hasRecordingFromDetails,
          recording_status: response.session?.recording_status || prev.recording_status,
        } : prev);
      }
    } catch (err: any) {
      console.error('Error loading call details:', err);
      toast({
        title: "Error",
        description: "Failed to load call details",
        variant: "destructive",
      });
    } finally {
      setLoadingCallData(false);
    }
  };

  /**
   * Handle recording click
   */
  const handleRecordingPress = async (recording: CallRecording) => {
    // Close customer details dialog first
    setSelectedCustomer(null);

    // Then open video player modal
    setSelectedRecording(recording);
    setShowVideoOverlay(true);
    setVideoLoading(true);
    setVideoError(null);

    try {
      // Check if WebM format
      if (recording.url?.toLowerCase().endsWith('.webm')) {
        throw new Error('WEBM_FORMAT');
      }

      // Get video URL
      let url = recording.url;

      // If not a full URL, get from storage
      if (!url.startsWith('http')) {
        const { data: signedData, error } = await supabase.storage
          .from('call-recordings')
          .createSignedUrl(url, 3600);

        if (error) throw error;
        url = signedData?.signedUrl || '';
      }

      setVideoUrl(url);
    } catch (err: any) {
      console.error('Error loading video:', err);
      if (err.message === 'WEBM_FORMAT') {
        setVideoError('WebM format videos are not supported. This recording can be viewed on desktop.');
      } else {
        setVideoError('Failed to load video');
      }
    } finally {
      setVideoLoading(false);
    }
  };

  /**
   * Close video overlay
   */
  const closeVideoOverlay = () => {
    setShowVideoOverlay(false);
    setSelectedRecording(null);
    setVideoUrl(null);
    setVideoError(null);
  };

  /**
   * Format date and time
   */
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  /**
   * Format duration
   */
  const formatDuration = (seconds: number | null) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  /**
   * Clear add form
   */
  const clearAddForm = () => {
    setFirstName("");
    setLastName("");
    setPhoneNumber("");
    setEmail("");
    setCountryCode("+1");
    setAssignTo(currentUserId || "");
  };

  /**
   * Open add modal
   */
  const openAddModal = () => {
    clearAddForm();
    setIsAddModalOpen(true);
  };

  /**
   * Handle add customer
   */
  const handleAddCustomer = async () => {
    // Validation
    if (!firstName.trim() || !lastName.trim()) {
      toast({
        title: "Validation Error",
        description: "First name and last name are required.",
        variant: "destructive",
      });
      return;
    }

    if (!phoneNumber.trim() || !isValidPhone(phoneNumber)) {
      toast({
        title: "Validation Error",
        description: "Phone number must be exactly 10 digits.",
        variant: "destructive",
      });
      return;
    }

    if (email && !isValidEmail(email)) {
      toast({
        title: "Validation Error",
        description: "Email address is not valid.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);

    try {
      const normalizedPhone = sanitizePhoneInput(phoneNumber.trim());
      // Ensure country code doesn't have phone digits in it
      const cleanCountryCode = countryCode.replace(/[^+\d]/g, '').substring(0, 3); // Limit to +XX format
      const result = await createCustomer({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone_number: `${cleanCountryCode} ${normalizedPhone}`,
        email: email.trim() || null,
        assigned_to: assignTo || null,
      });

      if (result) {
        clearAddForm();
        setIsAddModalOpen(false);
        toast({
          title: "Success",
          description: "Customer added successfully!",
        });
      } else {
        // Show specific error from hook if available
        toast({
          title: "Error",
          description: customersError || "Failed to add customer. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('[ContactsPage] Error adding customer:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Enter edit mode
   */
  const enterEditMode = () => {
    if (!selectedCustomer) return;

    const nameParts = selectedCustomer.name.split(' ');
    setEditFirstName(selectedCustomer.first_name || nameParts[0] || "");
    setEditLastName(selectedCustomer.last_name || nameParts.slice(1).join(' ') || "");

    // Parse phone number - US only, always use +1
    let countryCode = "+1";
    let phoneDigits = "";

    const fullPhone = selectedCustomer.phone_number.replace(/\s+/g, ''); // Remove all spaces

    // Check if it starts with +1 (US country code)
    if (fullPhone.startsWith('+1')) {
      // Extract everything after "+1"
      phoneDigits = fullPhone.substring(2);
      countryCode = '+1';
    } else if (fullPhone.startsWith('+')) {
      // Has some other country code - strip it and use the digits
      phoneDigits = fullPhone.substring(1);
      countryCode = '+1';
    } else {
      // No country code present
      phoneDigits = fullPhone;
    }

    setEditPhoneNumber(phoneDigits);
    setEditCountryCode(countryCode);

    setEditEmail(selectedCustomer.email || "");
    setEditAssignTo(selectedCustomer.assigned_to || "");
    setIsEditMode(true);
  };

  /**
   * Handle update customer
   */
  const handleUpdateCustomer = async () => {
    if (!selectedCustomer) return;

    // Validation
    if (!editFirstName.trim() || !editLastName.trim()) {
      toast({
        title: "Validation Error",
        description: "First name and last name are required.",
        variant: "destructive",
      });
      return;
    }

    if (!editPhoneNumber.trim() || !isValidPhone(editPhoneNumber)) {
      toast({
        title: "Validation Error",
        description: "Phone number must be exactly 10 digits.",
        variant: "destructive",
      });
      return;
    }

    if (editEmail && !isValidEmail(editEmail)) {
      toast({
        title: "Validation Error",
        description: "Email address is not valid.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);

    try {
      const normalizedPhone = sanitizePhoneInput(editPhoneNumber.trim());
      // Ensure country code doesn't have phone digits in it
      const cleanCountryCode = editCountryCode.replace(/[^+\d]/g, '').substring(0, 3); // Limit to +XX format
      const fullName = `${editFirstName.trim()} ${editLastName.trim()}`.trim();

      const result = await updateCustomer(selectedCustomer.id, {
        name: fullName, // Combined full name for database
        first_name: editFirstName.trim(),
        last_name: editLastName.trim(),
        phone_number: `${cleanCountryCode} ${normalizedPhone}`,
        email: editEmail.trim() || null,
        assigned_to: editAssignTo || null,
      });

      if (result) {
        setSelectedCustomer(result);
        setIsEditMode(false);
        toast({
          title: "Success",
          description: "Customer updated successfully!",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to update customer",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('[ContactsPage] Error updating customer:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle delete customer
   */
  const handleDeleteCustomer = async () => {
    if (!selectedCustomer) return;

    setIsSaving(true);

    try {
      const success = await deleteCustomer(selectedCustomer.id);

      if (success) {
        setIsDetailModalOpen(false);
        setIsDeleteDialogOpen(false);
        setSelectedCustomer(null);
        toast({
          title: "Success",
          description: "Customer deleted successfully",
        });
      } else {
        toast({
          title: "Error",
          description: customersError || "Failed to delete customer",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('[ContactsPage] Error deleting customer:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Close detail modal
   */
  const closeDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedCustomer(null);
    setIsEditMode(false);
    setCallHistory([]);
  };

  /**
   * Fetch current user and role
   */
  useEffect(() => {
    const fetchUserRole = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      setCurrentUserId(user.id);

      // SECURITY: Use backend API instead of direct database query
      const userData = await apiClient.get<{ ok: boolean; userRole: { role?: string } | null }>('/user/me');
      if (userData?.userRole?.role) {
        setUserRole(userData.userRole.role as 'admin' | 'user');
      }
    };

    fetchUserRole();
  }, []);

  // Set default assignTo to current user when teamMembers loads
  useEffect(() => {
    if (teamMembers.length > 0 && !assignTo && currentUserId) {
      setAssignTo(currentUserId);
    }
  }, [teamMembers, assignTo, currentUserId]);

  // Check for customer param in URL (deep linking from CallsPage)
  useEffect(() => {
    const customerId = searchParams.get('customer');
    if (!customerId) return;

    // First check if customer is in local array
    if (customers.length > 0) {
      const customer = customers.find(c => c.id === customerId);
      if (customer) {
        handleCustomerClick(customer);
        return;
      }
    }

    // If not found locally, fetch from backend (might be created by different team member)
    const fetchCustomerById = async () => {
      try {
        const response = await apiClient.get<{ ok: boolean; customer: Customer }>(
          API_ROUTES.CUSTOMER_BY_ID(customerId)
        );

        if (response.ok && response.customer) {
          handleCustomerClick(response.customer);
        } else {
          toast({
            title: "Customer Not Found",
            description: "This customer may have been deleted or you don't have access.",
            variant: "destructive"
          });
        }
      } catch (error) {
        console.error('Error fetching customer:', error);
        toast({
          title: "Error",
          description: "Failed to load customer profile.",
          variant: "destructive"
        });
      }
    };

    fetchCustomerById();
  }, [searchParams, customers]);

  /**
   * Check subscription access (trial users can access)
   */
  if (!canAccess) {
    return (
      <SubscriptionRequiredScreen
        feature="Contacts"
        reason={reason}
        onUpgrade={() => navigate('/profile?tab=billing')}
      />
    );
  }

  /**
   * Render loading state
   */
  if (loading && customers.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-8rem)] space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Loading customers...</p>
      </div>
    );
  }

  /**
   * Render error state
   */
  if (customersError && customers.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-8rem)] space-y-4">
        <p className="text-destructive">{customersError}</p>
        <Button onClick={() => refresh()} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-6xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Customers</h1>
        <p className="text-muted-foreground">Manage your customer list</p>
      </div>

      {/* Search and Action Bar */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search customers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Sort Button */}
        <Button
          variant="outline"
          onClick={toggleSort}
          className={sortOrder ? 'bg-primary/10' : ''}
        >
          <ArrowUpDown className="h-4 w-4 mr-2" />
          {sortOrder === 'asc' ? 'A-Z' : sortOrder === 'desc' ? 'Z-A' : 'Sort'}
        </Button>

        {/* Team Member Filter (only show for admins) */}
        {userRole === 'admin' && (
          <Select
            value={selectedTeamMember}
            onValueChange={setSelectedTeamMember}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by member" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Customers</SelectItem>
              {teamMembers.map((member) => (
                <SelectItem key={member.id} value={member.id}>
                  {member.name} ({member.role})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Refresh Button */}
        <Button
          variant="outline"
          onClick={() => refresh()}
          disabled={refreshing}
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
        </Button>

        {/* Add Button */}
        <Button onClick={openAddModal} className="bg-primary">
          <Plus className="h-4 w-4 mr-2" />
          Add Customer
        </Button>
      </div>

      {/* Customers List */}
      {filteredAndSortedCustomers.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 space-y-2">
          <p className="text-lg text-muted-foreground">
            {searchQuery ? 'No customers found' : 'No customers yet'}
          </p>
          <p className="text-sm text-muted-foreground">
            {searchQuery ? 'Try adjusting your search' : 'Add your first customer to get started'}
          </p>
        </div>
      ) : (
        <div className="bg-transparent rounded-lg border-0">
          {(() => {
            // Group customers by first letter
            const grouped = filteredAndSortedCustomers.reduce((acc, customer) => {
              const firstLetter = customer.name.charAt(0).toUpperCase();
              if (!acc[firstLetter]) acc[firstLetter] = [];
              acc[firstLetter].push(customer);
              return acc;
            }, {} as Record<string, typeof filteredAndSortedCustomers>);

            // Sort letters based on sortOrder: A-Z (asc) or Z-A (desc)
            const sortedLetters = Object.keys(grouped).sort((a, b) => {
              if (sortOrder === 'desc') {
                return b.localeCompare(a); // Z-A
              }
              return a.localeCompare(b); // A-Z (default)
            });

            return sortedLetters.map((letter) => (
              <div key={letter}>
                {/* Section Header */}
                <div className="px-4 py-2 border-b border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-500">{letter}</h3>
                </div>

                {/* Contacts in this section */}
                {grouped[letter].map((customer, index) => (
                  <div key={customer.id}>
                    <div
                      className="px-4 py-3 cursor-pointer hover:bg-[#94ACF8]/10 transition-colors"
                      onClick={() => handleCustomerClick(customer)}
                    >
                      <p className="text-base text-gray-900">{customer.name}</p>
                    </div>
                    {/* Divider after every row */}
                    <div className="border-b border-gray-200 ml-4" />
                  </div>
                ))}
              </div>
            ));
          })()}
        </div>
      )}

      {/* Add Customer Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Customer</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* First Name + Last Name */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  placeholder="John"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  placeholder="Doe"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>

            {/* Phone Number */}
            <div className="space-y-2">
              <Label htmlFor="phoneNumber">Phone Number *</Label>
              <div className="flex gap-2">
                <Input
                  className="w-20"
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                />
                <Input
                  id="phoneNumber"
                  placeholder="(555) 123-4567"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(sanitizePhoneInput(e.target.value))}
                  className="flex-1"
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="customer@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {/* Assign to User (Admin only) */}
            {userRole === 'admin' && (
              <div className="space-y-2">
                <Label htmlFor="assignTo">Assign to User</Label>
                <Select value={assignTo} onValueChange={setAssignTo}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a user" />
                  </SelectTrigger>
                  <SelectContent>
                    {teamMembers.length ? (
                      teamMembers.map((member) => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.name} ({member.role})
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="unavailable" disabled>
                        No users available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsAddModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddCustomer} disabled={isSaving}>
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Customer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer Detail Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={(open) => !open && closeDetailModal()}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center justify-between pr-8">
              <span>{isEditMode ? 'Edit Customer' : 'Customer Details'}</span>
              {!isEditMode && (
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 -mr-2" onClick={enterEditMode}>
                  <Edit className="h-4 w-4" />
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedCustomer && (
            <div className="space-y-6 py-4 overflow-y-auto flex-1">
              {!isEditMode ? (
                <>
                  {/* View Mode - Compact */}
                  <div>
                    <h3 className="font-semibold text-lg">{selectedCustomer.name}</h3>
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-2 border-t pt-4">
                    <div>
                      <label className="text-xs text-muted-foreground">Phone</label>
                      <p className="text-sm font-medium">{selectedCustomer.phone_number}</p>
                    </div>
                    {selectedCustomer.email && (
                      <div>
                        <label className="text-xs text-muted-foreground">Email</label>
                        <p className="text-sm font-medium">{selectedCustomer.email}</p>
                      </div>
                    )}
                  </div>

                  {/* Call History Section */}
                  <div className="border-t pt-4">
                    <h3 className="text-lg font-semibold mb-4">Call History</h3>

                    {loadingCallHistory && callHistory.length === 0 ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-primary mr-2" />
                        <span className="text-muted-foreground">Loading recordings...</span>
                      </div>
                    ) : callHistory.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No calls yet
                      </div>
                    ) : (
                      <>
                        <div className="space-y-2">
                          {callHistory.map((call: any) => (
                            <Card
                              key={call.id}
                              className="cursor-pointer hover:shadow-md transition-all duration-200 hover:border-primary/50"
                              onClick={() => handleCallClick(call)}
                            >
                              <CardContent className="p-3">
                                <div className="flex items-start justify-between gap-4">
                                  {/* Left: Call Info */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <UserIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                      <h3 className="font-semibold text-base truncate">
                                        {call.customer_name || 'Unknown Customer'}
                                      </h3>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                      <Calendar className="h-3 w-3" />
                                      {formatDateTime(call.created_at)}
                                    </div>
                                    {call.duration_seconds && (
                                      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-0.5">
                                        <Clock className="h-3 w-3" />
                                        {formatDuration(call.duration_seconds)}
                                      </div>
                                    )}
                                  </div>

                                  {/* Right: Metadata */}
                                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                                    {call.type && (
                                      <Badge className={`text-xs ${
                                        call.type === 'recording-request' ? 'bg-orange-100 text-orange-800' :
                                        call.type === 'scheduled-call' ? 'bg-green-100 text-green-800' :
                                        'bg-purple-100 text-purple-800'
                                      }`}>
                                        {call.type === 'recording-request' ? 'Recording' :
                                         call.type === 'scheduled-call' ? 'Scheduled' : 'Live Call'}
                                      </Badge>
                                    )}
                                    {call.has_recording && (
                                      <div className="flex items-center gap-1 text-xs text-green-600">
                                        <PlayCircle className="h-3 w-3" />
                                        <span>Recording</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>

                        {hasMoreCalls && (
                          <div className="flex justify-center mt-4">
                            <Button
                              variant="outline"
                              onClick={(e) => { e.stopPropagation(); loadMoreCalls(); }}
                              disabled={loadingCallHistory}
                            >
                              {loadingCallHistory ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                'Load More'
                              )}
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex flex-col h-full">
                  {/* Scrollable Form Fields */}
                  <div className="flex-1 overflow-y-auto pr-2">
                    <div className="space-y-4">
                      {/* First Name + Last Name */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="editFirstName">First Name *</Label>
                          <Input
                            id="editFirstName"
                            value={editFirstName}
                            onChange={(e) => setEditFirstName(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="editLastName">Last Name *</Label>
                          <Input
                            id="editLastName"
                            value={editLastName}
                            onChange={(e) => setEditLastName(e.target.value)}
                          />
                        </div>
                      </div>

                      {/* Phone Number */}
                      <div className="space-y-2">
                        <Label htmlFor="editPhoneNumber">Phone Number *</Label>
                        <div className="flex gap-2">
                          <Input
                            className="w-20"
                            value={editCountryCode}
                            onChange={(e) => setEditCountryCode(e.target.value)}
                          />
                          <Input
                            id="editPhoneNumber"
                            value={editPhoneNumber}
                            onChange={(e) => setEditPhoneNumber(sanitizePhoneInput(e.target.value))}
                            className="flex-1"
                          />
                        </div>
                      </div>

                      {/* Email */}
                      <div className="space-y-2">
                        <Label htmlFor="editEmail">Email</Label>
                        <Input
                          id="editEmail"
                          type="email"
                          value={editEmail}
                          onChange={(e) => setEditEmail(e.target.value)}
                        />
                      </div>

                      {/* Assign to User (Admin only) */}
                      {userRole === 'admin' && (
                        <div className="space-y-2">
                          <Label htmlFor="editAssignTo">Assign to User</Label>
                          <Select value={editAssignTo} onValueChange={setEditAssignTo}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a user" />
                            </SelectTrigger>
                            <SelectContent>
                              {teamMembers.length ? (
                                teamMembers.map((member) => (
                                  <SelectItem key={member.id} value={member.id}>
                                    {member.name} ({member.role})
                                  </SelectItem>
                                ))
                              ) : (
                                <SelectItem value="unavailable" disabled>
                                  No users available
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons Section - Fixed at bottom, no scroll */}
                  <div className="flex-shrink-0 space-y-4 pt-4 border-t mt-4">
                    {/* Save/Cancel Buttons */}
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setIsEditMode(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleUpdateCustomer} disabled={isSaving}>
                        {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        Save Changes
                      </Button>
                    </div>

                    {/* Delete Button - Separate from Save/Cancel */}
                    <div className="border-t pt-4">
                      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" className="w-full text-destructive border-destructive hover:bg-destructive hover:text-white">
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Customer
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Customer</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete {selectedCustomer.name}? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={handleDeleteCustomer} className="bg-destructive">
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Call Details Modal */}
      <Dialog open={!!selectedCallForDetails} onOpenChange={(open) => !open && setSelectedCallForDetails(null)} aria-describedby={undefined}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Call Details</DialogTitle>
          </DialogHeader>

          {loadingCallData ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
            </div>
          ) : selectedCallForDetails ? (
            <div className="space-y-4 pb-8">
                {/* Colored Metric Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Customer Card */}
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-600 font-medium mb-1">Customer</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedCallForDetails.customer_name || 'Unknown'}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {selectedCallForDetails.phone_number || selectedCallForDetails.customer_phone || 'No phone'}
                    </p>
                  </div>

                  {/* Duration Card */}
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-600 font-medium mb-1">Duration</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedCallForDetails.duration_seconds ? formatDuration(selectedCallForDetails.duration_seconds) : 'N/A'}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {formatDateTime(selectedCallForDetails.start_time || selectedCallForDetails.created_at)}
                    </p>
                  </div>

                  {/* Recording Card */}
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <p className="text-sm text-purple-600 font-medium mb-1">
                      {allRecordings.length > 1 ? `Recordings (${allRecordings.length})` : 'Recording'}
                    </p>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedCallForDetails.has_recording || allRecordings.length > 0
                        ? 'Available'
                        : selectedCallForDetails.recording_status === 'recording'
                          ? 'Processing'
                          : 'Not saved'}
                    </p>
                    {!selectedCallForDetails.has_recording && allRecordings.length === 0 && (
                      <p className="text-sm text-gray-500 mt-1">
                        {selectedCallForDetails.recording_status === 'recording'
                          ? 'Recording is being uploaded...'
                          : 'No recording available'}
                      </p>
                    )}
                  </div>
                </div>

                {/* View Recording Button(s) */}
                {(selectedCallForDetails.has_recording || allRecordings.length > 0) && (
                  <div className="space-y-2">
                    <Label className="text-base mb-2 block">
                      {allRecordings.length > 1 ? `Recordings (${allRecordings.length})` : 'Recording'}
                    </Label>
                    {allRecordings.length > 1 ? (
                      // Multiple recordings - show list
                      <div className="space-y-2">
                        {allRecordings.map((recording, index) => (
                          <Button
                            key={recording.id}
                            variant="outline"
                            onClick={() => {
                              if (!recording.url) {
                                toast({
                                  title: "Error",
                                  description: "Recording URL not available",
                                  variant: "destructive",
                                });
                                return;
                              }
                              setVideoUrl(recording.url);
                              setSelectedRecording(selectedCallForDetails);
                              setSelectedCallForDetails(null);
                              setTimeout(() => {
                                setShowVideoOverlay(true);
                              }, 100);
                            }}
                            className="w-full justify-start"
                            disabled={!recording.url}
                          >
                            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Recording {index + 1}
                            {recording.duration_seconds && (
                              <span className="ml-auto text-xs text-gray-500">
                                {Math.floor(recording.duration_seconds / 60)}:{String(recording.duration_seconds % 60).padStart(2, '0')}
                              </span>
                            )}
                          </Button>
                        ))}
                      </div>
                    ) : (
                      // Single recording - show single button
                      <Button
                        variant="default"
                        onClick={() => {
                          setSelectedRecording(selectedCallForDetails);
                          setSelectedCallForDetails(null);
                          setTimeout(() => {
                            setShowVideoOverlay(true);
                          }, 100);
                        }}
                        className="w-full bg-purple-600 hover:bg-purple-700"
                      >
                        <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        View Recording
                      </Button>
                    )}
                  </div>
                )}

                {/* Call Notes */}
                {callNotes && (
                  <div>
                    <Label className="text-base mb-2 block">Call Notes</Label>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{callNotes}</p>
                    </div>
                  </div>
                )}

                {/* AI Summary Section */}
                {!usage?.subscribed && !usage?.is_pro_trial ? (
                  <div className="p-6 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border-2 border-blue-200 text-center space-y-3">
                    <Sparkles className="h-8 w-8 text-purple-600 mx-auto" />
                    <h3 className="text-lg font-semibold text-gray-900">AI-Powered Call Summaries</h3>
                    <p className="text-sm text-gray-600">
                      Get instant AI-generated summaries and transcripts of your calls
                    </p>
                    <Button
                      onClick={() => navigate('/profile?tab=billing')}
                      className="mt-4"
                    >
                      Subscribe to unlock AI features
                    </Button>
                  </div>
                ) : aiSummary.length > 0 ? (
                  <div>
                    <Label className="text-base mb-3 block flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-purple-600" />
                      AI Summary
                    </Label>
                    <div className="space-y-2">
                      {aiSummary.map((point, index) => (
                        <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                          <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-medium">
                            {index + 1}
                          </div>
                          <p className="text-sm text-gray-700 leading-relaxed">{point}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {/* Transcript */}
                {aiTranscript && (
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="w-full">View Full Transcript</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[80vh]">
                      <DialogHeader>
                        <DialogTitle>Call Transcript</DialogTitle>
                        <DialogDescription>Complete conversation transcript</DialogDescription>
                      </DialogHeader>
                      <ScrollArea className="h-[60vh] pr-4">
                        <div className="whitespace-pre-wrap text-sm leading-relaxed">
                          {aiTranscript}
                        </div>
                      </ScrollArea>
                    </DialogContent>
                  </Dialog>
                )}

                {/* Snapshots Grid */}
                {snapshots.length > 0 && (
                  <div>
                    <Label className="text-base mb-3 block">Call Snapshots ({snapshots.length})</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {snapshots.map((s) => (
                        <a
                          key={s.name}
                          href={s.url}
                          download
                          className="rounded-lg border-2 border-gray-200 overflow-hidden bg-white hover:border-blue-400 transition-colors group"
                        >
                          <img
                            src={s.url}
                            alt={s.name}
                            className="w-full h-32 object-cover"
                            loading="lazy"
                          />
                          <div className="p-2 bg-gray-50 group-hover:bg-blue-50 transition-colors">
                            <span className="text-xs text-gray-600 group-hover:text-blue-600">
                              Download
                            </span>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Video Player Modal */}
      {videoUrl && selectedRecording && (
        <VideoPlayerModal
          open={showVideoOverlay}
          onOpenChange={setShowVideoOverlay}
          videoUrl={videoUrl}
          callDetails={{
            customerName: selectedRecording.customer_name || 'Guest',
            customerEmail: selectedRecording.customer_email,
            customerPhone: selectedRecording.customer_phone,
            startTime: selectedRecording.created_at,
            duration: selectedRecording.duration_seconds,
            type: selectedRecording.type,
            notes: selectedRecording.notes,
          }}
        />
      )}
    </div>
  );
}
